import {
  Box,
  HStack,
  Heading,
  Stack,
  Text,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react"
import { AvatarCircleIcon } from "@pathwright/pathicons"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { AvatarUser } from "@pathwright/ux/src/components/avatar/types"
import {
  CarouselGallery,
  GridGallery
} from "@pathwright/ux/src/components/gallery/Gallery"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { Fragment, useEffect, useRef, useState } from "react"
import CoverPageDivider from "./CoverPageDivider"
import { ImmersiveTriggerButton } from "./CoverPageImmersive"
import { useCoverPageContext } from "./CoverPageProvider"
import { COVER_PAGE_PADDING } from "./constants"
import { CoverPageContributor } from "./types"
import { getAvatarUserFromContributor } from "./utils"

const ContributorBio = ({
  bio,
  truncated,
  id,
  maxHeight
}: {
  bio: string
  maxHeight?: number
  id?: number
  truncated?: boolean
}) => {
  return (
    <HTMLText
      html={bio}
      size="xs"
      color="PRIMARY_GRAY"
      w="100%"
      truncate={truncated}
      truncateProps={{
        maxHeight,
        ellipsis: true,
        renderMore: () => (
          <ImmersiveTriggerButton
            route={{
              name: "contributor",
              id: id as number
            }}
          />
        )
      }}
    />
  )
}

// Note: "contributors" have been renamed to "contributors" on the frontend
const ContributorCard = ({
  contributor,
  full,
  disableTruncation
}: {
  contributor: CoverPageContributor
  full?: boolean
  disableTruncation?: boolean
}) => {
  const [isTruncated, setIsTruncated] = useState(false)
  const bioRef = useRef<HTMLDivElement>(null)
  const { isMobile, immersiveActive, onOpenImmersive } = useCoverPageContext()

  useEffect(() => {
    if (contributor.bio && bioRef.current) {
      const element = bioRef.current

      const checkOverflow = () => {
        // Measure overflow using scrollHeight vs clientHeight on the bio box only
        const constrainedHeight = element.clientHeight
        const fullHeight = element.scrollHeight

        const isOverflowing = fullHeight > constrainedHeight + 1 // 1px tolerance
        setIsTruncated(isOverflowing)
      }

      // Initial measure
      checkOverflow()

      // Observe size changes of the element to re-check overflow
      const resizeObserver = new ResizeObserver(() => {
        checkOverflow()
      })
      resizeObserver.observe(element)

      return () => {
        resizeObserver.disconnect()
      }
    } else {
      setIsTruncated(false)
    }
  }, [contributor.bio, isMobile])

  const shouldTruncate = !disableTruncation

  const handleOpenImmersive = (event: React.MouseEvent<HTMLDivElement>) => {
    if (immersiveActive) return

    onOpenImmersive?.({
      name: "contributor",
      id: contributor.id as number
    })
  }

  return (
    <Stack
      className="ContributorCard"
      direction={full ? "row" : "column"}
      align={"start"}
      spacing={full ? "30px" : 2}
      h="100%"
      w="100%"
      maxW={full || isMobile ? "100%" : "200px"}
      key={contributor.id}
      overflow={full ? "hidden" : "visible"}
      px={4}
      pt={full ? 4 : 2}
      pb={full ? 4 : 2}
      onClick={handleOpenImmersive}
      cursor={immersiveActive ? "default" : "pointer"}
      _hover={{
        bgColor: "white",
        borderRadius: "card",
        boxShadow: "card"
      }}
    >
      <UserAvatar
        className="ContributorAvatar"
        user={getAvatarUserFromContributor(contributor) as AvatarUser}
        size="xxl"
        w="150px"
        maxW="150px"
        h="150px"
        maxH="150px"
        icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
      />
      <VStack
        gap={0}
        className="ContributorCardContent"
        w="100%"
        align={"start"}
        h={full && !isMobile ? "150px" : "auto"}
      >
        <VStack gap={0} w="100%" align={"start"}>
          <Heading
            as="h3"
            size="h4"
            mt={0}
            mb={full ? "5px" : 0}
            color="PRIMARY_GRAY"
            fontWeight="600"
            textAlign={"left"}
          >
            {contributor.salutation ? `${contributor.salutation} ` : ""}
            {contributor.full_name}
          </Heading>
          {contributor.title && (
            <Text
              mt={0}
              mb="5px"
              color="gray.500"
              textAlign={"left"}
              cursor="text"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
            >
              {contributor.title}
            </Text>
          )}
        </VStack>
        {contributor.bio && (
          <Box
            sx={{
              "p:first-of-type": {
                marginTop: "0"
              },
              "p:last-of-type": {
                marginBottom: "0"
              }
            }}
            position="relative"
            w="100%"
            h="100%"
            cursor="text"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            ref={bioRef}
            textAlign="left"
            maxH={!shouldTruncate ? "auto" : `calc(1.4rem * 4)`}
            overflow={!shouldTruncate ? "visible" : "hidden"}
          >
            <ContributorBio
              bio={contributor.bio!}
              truncated={shouldTruncate}
              maxHeight={!shouldTruncate ? 0 : 1.4 * 16 * 4}
              id={contributor.id as number}
            />
          </Box>
        )}
      </VStack>
    </Stack>
  )
}

const ContributorGridCard = ({
  contributor
}: {
  contributor: CoverPageContributor
}) => {
  return (
    <VStack
      className="ContributorGridCard"
      align="stretch"
      borderRadius="card"
      bgColor="backgroundSecondary"
      boxShadow="card"
      overflow="hidden"
      p={"20px"}
      h="100%"
      sx={{
        ".ContributorCard": {
          w: "100%",
          maxW: "100%",
          bgColor: "backgroundSecondary",
          borderRadius: "card",
          boxShadow: "card"
        },
        ".ContributorAvatar": {
          maxW: "100px",
          maxH: "100px"
        }
      }}
    >
      <HStack h="100px">
        <UserAvatar
          className="ContributorAvatar"
          user={getAvatarUserFromContributor(contributor) as AvatarUser}
          size="xxl"
          w="100px"
          maxW="100px"
          h="100px"
          maxH="100px"
          icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
        />
        <VStack align="start" gap={0}>
          <Heading
            as="h3"
            size="h4"
            mt={0}
            mb={0}
            color="PRIMARY_GRAY"
            fontWeight="600"
            textAlign={"left"}
          >
            {contributor.full_name}
          </Heading>
          <Text>{contributor.title}</Text>
        </VStack>
      </HStack>
      <Box w="100%">
        <ContributorBio
          bio={contributor.bio!}
          truncated={true}
          maxHeight={1.4 * 16 * 6}
          id={contributor.id as number}
        />
      </Box>
    </VStack>
  )
}

const ContributorCardStack = () => {
  const { contributors, isMobile } = useCoverPageContext()

  return (
    <VStack
      align={isMobile ? "center" : "start"}
      spacing={2}
      px={COVER_PAGE_PADDING}
    >
      {contributors.map((contributor, index) => (
        <Fragment key={contributor.id}>
          <ContributorCard
            key={contributor.id}
            contributor={contributor}
            full={!isMobile}
          />
          {index !== contributors.length - 1 && (
            <CoverPageDivider horizontalMargin={"0"} verticalMargin={"20px"} />
          )}
        </Fragment>
      ))}
    </VStack>
  )
}

const CoverPageContributors = () => {
  const {
    contributors: contributors,
    contributorSectionRef,
    immersiveActive
  } = useCoverPageContext()

  const itemsPerRow = useBreakpointValue({
    base: 1,
    sm: immersiveActive ? 2 : 3,
    lg: immersiveActive ? 3 : 5
  })

  if (!contributors?.length) return null

  const contributorsHaveBios = contributors.some(
    (contributor) => !!contributor.bio
  )

  return (
    <>
      {!immersiveActive && (
        <CoverPageDivider title="Contributors" ref={contributorSectionRef} />
      )}
      {immersiveActive ? (
        <Box
          sx={{
            ".gallery-grid-item": {
              h: "100%"
            }
          }}
          m="0 auto"
          maxW={"1000px"}
        >
          <GridGallery
            items={contributors}
            renderItem={(contributor: CoverPageContributor) => (
              <ContributorGridCard contributor={contributor} />
            )}
            gap={20}
            itemsFillWidth={false}
            spacingY="0"
            spacingX={0}
            itemsPerRow={itemsPerRow}
          />
        </Box>
      ) : contributorsHaveBios ? (
        <ContributorCardStack />
      ) : (
        <CarouselGallery
          items={contributors}
          renderItem={(contributor: CoverPageContributor) => {
            return <ContributorCard contributor={contributor} full={false} />
          }}
          gap={30}
          spacingY="0"
          itemsFillWidth={false}
          spacingX={COVER_PAGE_PADDING}
          itemsPerRow={itemsPerRow}
          enableStagger
        />
      )}
    </>
  )
}

export default CoverPageContributors

// Immersive: Focused contributor view
export const CoverPageContributorImmersive = ({
  id
}: {
  id?: number | string
}) => {
  const { contributors, isMobile } = useCoverPageContext()

  if (!contributors?.length) return null

  // Use the first contributor if no id is provided
  const normalizedId = !id
    ? contributors[0].id
    : typeof id === "string"
    ? Number(id)
    : id
  const contributor = contributors?.find((a) => a.id === normalizedId)

  if (!contributor) return null

  return (
    <Box
      key={`contributor-${id}`}
      w="100%"
      h="100%"
      pos="relative"
      mx="auto"
      px={0}
      className="CoverPageContributorImmersive"
      bgColor="backgroundSecondary"
      borderRadius="card"
      maxHeight="100%"
      overflow="auto"
      sx={{
        "p, div, span": {
          color: "primary"
        },
        ".ContributorCard": {
          overflow: "auto",
          p: "30px"
        },
        ".ContributorCardContent": {
          h: "auto"
        }
      }}
    >
      {/* Focused contributor: never truncate; row on desktop, column on mobile */}
      <ContributorCard
        contributor={contributor}
        full={!isMobile}
        disableTruncation
      />
    </Box>
  )
}
